import { useEffect, useMemo, useState } from 'react'
import { Typography } from '@mui/material'
import { WarningRounded } from '@mui/icons-material'
import { apiGet } from '../../../generic/Api_Functions'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import SmartSelect from '../../../generic/smartSelect/SmartSelect'
import createDialogTitle from '../../../generic/utilities/dialogUtil'

/**
 * @typedef {import('../../../generic/utilities/objectStructures/workflow/workflow').Technology} Technology Technology object
 */

/**
 * Dialog used to change the technology of a JOB node, keeping the node identity in the editor.
 * @param {Object} props Props object
 * @param {boolean} props.open Variable to control dialog visibility
 * @param {Function} props.onClose Function to close the dialog
 * @param {Technology} props.currentTechnology Job's current technology
 * @param {function(Technology)} props.onAccept Function to execute with the selected technology on accept
 * @returns
 */
const ChangeTechnologyDialog = ({ open, onClose, currentTechnology, onAccept }) => {
  const { t_ } = useSmartTranslation()
  const [technologies, setTechnologies] = useState([])
  const [selectedTechnology, setSelectedTechnology] = useState(null)
  const isCurrentTechnologySensitive = currentTechnology?.sensitive === true

  useEffect(() => {
    if (!open || isCurrentTechnologySensitive) {
      setSelectedTechnology(null)
      return
    }
    const fetchTechnologies = async () => {
      const result = await apiGet('technologiesWorkflow/noPagination')
      setTechnologies(result ?? [])
    }
    fetchTechnologies()
  }, [open, isCurrentTechnologySensitive])

  const disabledOptions = useMemo(
    () => Promise.resolve(
      technologies.filter((technology) => technology.id === currentTechnology?.id || technology.sensitive === true)
    ),
    [technologies, currentTechnology]
  )

  const handleAccept = () => {
    if (!selectedTechnology) {
      return
    }
    onAccept(selectedTechnology)
    onClose()
  }

  if (isCurrentTechnologySensitive) {
    return (
      <SmartDialog
        setOpen={open}
        title={createDialogTitle({ title: t_('Warning'), icon: WarningRounded, color: 'warning' })}
        message={t_('The technology of this job cannot be changed because its current technology is sensitive')}
        accept={false}
        cancel={false}
        close
        closeCallback={onClose}
      />
    )
  }

  return (
    <SmartDialog
      setOpen={open}
      title={t_('Change technology')}
      accept
      acceptCallback={handleAccept}
      cancelCallback={onClose}
      renderComponent={
        <>
          <Typography sx={{ mb: 1 }}>{t_('Select the new technology for this job')}</Typography>
          <SmartSelect
            label={t_('Target technology')}
            value={selectedTechnology}
            onChange={setSelectedTechnology}
            selectableOptions={technologies}
            disabledOptions={disabledOptions}
          />
        </>
      }
    />
  )
}

export default ChangeTechnologyDialog
